"use client";
import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const data = [
  { name: "Industry", value: 42, color: "#858786" },
  { name: "Transport", value: 35, color: "#CDD24D" },
  { name: "Residential", value: 14, color: "#4A4A4A" },
  { name: "Commercial", value: 7, color: "#76B82D" },
  { name: "Agriculture & others", value: 2, color: "#00843D" },
];

const renderCustomizedLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, name, value } = props;

  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);

  // 🔥 ONLY FOR MOBILE + TABLET spacing fix
  const isSmallScreen = typeof window !== "undefined" ? window.innerWidth < 1300 : false;

  const offset = isSmallScreen ? 6 : 5;
  const elbow = isSmallScreen ? 20 : 25;
  const horizontal = isSmallScreen ? 20 : 30;

  const sx = cx + (outerRadius + offset) * cos;
  const sy = cy + (outerRadius + offset) * sin;

  const mx = cx + (outerRadius + elbow) * cos;
  const my = cy + (outerRadius + elbow) * sin;

  const ex = mx + (cos >= 0 ? horizontal : -horizontal);

  // vertical adjustments (unchanged)
  let ey = my;
  if (name === "Agriculture & others") ey -= 15;
  if (name === "Commercial") ey += 5;
  if (name === "Residential") ey += 25;

  const boxWidth = isSmallScreen ? Math.max(75, name.length * 5 + 6) : Math.max(85, name.length * 6 + 9);
  const boxHeight = isSmallScreen ? 28 : 36;

  const rectX = cos >= 0 ? ex : ex - boxWidth;
  const rectY = ey - boxHeight / 2;

  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke="#aaa"
        fill="none"
        strokeWidth={1}
      />
      <circle cx={ex} cy={ey} r={2} fill="#aaa" />

      <rect
        x={rectX}
        y={rectY}
        width={boxWidth}
        height={boxHeight}
        rx={4}
        fill="white"
        stroke="#ccc"
        style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.1))" }}
      />

      <text
        x={rectX + boxWidth / 2}
        y={isSmallScreen ? ey - 2 : ey - 3}
        textAnchor="middle"
        fill="#333"
        style={{ fontSize: isSmallScreen ? 9 : 11, fontWeight: 700, fontFamily: "Arial" }}
      >
        {name}
      </text>

      <text
        x={rectX + boxWidth / 2}
        y={isSmallScreen ? ey + 8 : ey + 11}
        textAnchor="middle"
        fill="#666"
        style={{ fontSize: isSmallScreen ? 9 : 11, fontFamily: "Arial" }}
      >
        {value}%
      </text>
    </g>
  );
};

const DoughnutChart: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isHideLabel, setIsHideLabel] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [isRadiusReduced, setIsRadiusReduced] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1170);
      setIsHideLabel(width < 640);
      setIsSmallMobile(width < 480);
      setIsRadiusReduced(width >= 1024 && width <= 1200);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!isMounted) return <div className="w-full h-full bg-[#f9f9f9]" />;

  return (
    <div
      className="w-full flex flex-col justify-center bg-[#f9f9f9] p-4 overflow-hidden"
      style={{ height: isMobile ? (isSmallMobile ? 380 : 420) : "100%" }}
    >
      <h3
        style={{
          textAlign: "center",
          fontSize: isSmallMobile ? 11 : isHideLabel ? 12 : 14,
          fontWeight: 600,
          marginBottom: 10,
        }}
      >
        Total Final Energy Consumption (455.8 Mtoe)
      </h3>

      <ResponsiveContainer width="100%" height="90%">
        <PieChart margin={isHideLabel ? { top: 10, left: 10, right: 10, bottom: 10 } : { top: 20, left: 40, right: 70, bottom: 20 }}>
          {/* ✅ MOBILE TOOLTIP */}
          <Tooltip formatter={(value: any, name: any) => [`${value}%`, name]} />

          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={isSmallMobile ? 35 : isHideLabel ? 50 : isRadiusReduced ? 50 : 65}
            outerRadius={isSmallMobile ? 60 : isHideLabel ? 80 : isRadiusReduced ? 80 : 105}
            dataKey="value"
            stroke="#fff"
            strokeWidth={2}
            tabIndex={-1}
            focusable={false}
            style={{ outline: "none" }}
            label={isHideLabel ? false : renderCustomizedLabel}
            labelLine={!isHideLabel}
            isAnimationActive={false}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DoughnutChart;
